import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Grid, Card, CardContent, Typography, Button, IconButton, Tooltip,
  CircularProgress, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Switch,
} from "@mui/material";
import {
  Add, Refresh, ContentCopy, VpnKey, Edit, CheckCircle, Error, Tune,
} from "@mui/icons-material";

// Linha de canal com chip toggle + input numerico de limite
function renderChannelRow(ch, planTenant, togglePlanChannel, setPerChannelMax) {
  const active = planTenant.plan?.[ch.id];
  const perCh = planTenant.plan?.maxConnectionsPerChannel || {};
  const globalMax = planTenant.plan?.maxConnections ?? 10;
  const channelMax = perCh[ch.id];
  const usingChannelLimit = typeof channelMax === "number";

  return (
    <Box key={ch.id} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.7, py: 0.5 }}>
      <Chip
        size="small"
        label={ch.label}
        onClick={() => togglePlanChannel(ch.id)}
        sx={{
          cursor: "pointer", fontWeight: 500, minWidth: 110, justifyContent: "flex-start",
          bgcolor: active ? "rgba(37,211,102,0.15)" : "rgba(255,255,255,0.04)",
          color: active ? "#25D366" : "#6B7280",
          border: `1px solid ${active ? "rgba(37,211,102,0.3)" : "rgba(255,255,255,0.08)"}`,
        }}
      />
      <Typography sx={{ fontSize: 10, color: "#6B7280", flex: 1 }}>
        Conexoes:
      </Typography>
      <input
        type="number"
        min="0"
        placeholder={`global (${globalMax})`}
        defaultValue={usingChannelLimit ? channelMax : ""}
        onBlur={(e) => setPerChannelMax(ch.id, e.target.value)}
        disabled={!active}
        style={{
          width: 80,
          padding: "5px 8px",
          borderRadius: 6,
          border: usingChannelLimit ? "1px solid rgba(107,72,255,0.4)" : "1px solid rgba(255,255,255,0.1)",
          backgroundColor: active ? "#1a1d29" : "#0e1018",
          color: active ? "#fff" : "#4B5563",
          fontSize: 12,
          outline: "none",
          textAlign: "center",
        }}
      />
    </Box>
  );
}

const ALL_CHANNELS = [
  { id: "whatsapp", label: "WhatsApp", group: "meta" },
  { id: "facebook", label: "Facebook", group: "meta" },
  { id: "instagram", label: "Instagram", group: "meta" },
  { id: "telegram", label: "Telegram", group: "outros" },
  { id: "email", label: "Email", group: "outros" },
  { id: "voip", label: "VoIP", group: "outros" },
  { id: "sip", label: "SIP Trunk", group: "outros" },
  { id: "tiktok", label: "TikTok", group: "outros" },
  { id: "sms", label: "SMS", group: "outros" },
  { id: "google_business", label: "Google Business", group: "outros" },
  { id: "mercadolivre", label: "Mercado Livre", group: "outros" },
  { id: "teams", label: "MS Teams", group: "outros" },
];
import { hubApi } from "../services/hubApi";

export default function TenantsPage({ showSnackbar }) {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", email: "", webhookUrl: "" });
  const [creating, setCreating] = useState(false);
  const [createdResult, setCreatedResult] = useState(null);
  const [editTenant, setEditTenant] = useState(null);
  const [planTenant, setPlanTenant] = useState(null);
  const [savingPlan, setSavingPlan] = useState(false);
  const [showKey, setShowKey] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await hubApi.adminListTenants();
      setTenants(data?.tenants || []);
    } catch (err) {
      const msg = err?.response?.data?.error || "Erro ao carregar tenants";
      showSnackbar?.(msg, "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!createForm.name) {
      showSnackbar?.("Nome obrigatorio", "error");
      return;
    }
    setCreating(true);
    try {
      const { data } = await hubApi.adminCreateTenant(createForm);
      setCreatedResult(data);
      load();
      showSnackbar?.(`Tenant ${data.tenantId} criado!`);
    } catch (err) {
      showSnackbar?.(err?.response?.data?.error || "Erro ao criar", "error");
    } finally {
      setCreating(false);
    }
  };

  const handleRegenerate = async (id, name) => {
    if (!window.confirm(`Regenerar chave do tenant "${name}"?\nA chave atual deixara de funcionar imediatamente.`)) return;
    try {
      const { data } = await hubApi.adminRegenerateKey(id);
      setCreatedResult({ tenantId: id, apiKey: data.apiKey, name, regenerated: true });
      load();
      showSnackbar?.("Chave regenerada");
    } catch (err) {
      showSnackbar?.(err?.response?.data?.error || "Erro ao regenerar", "error");
    }
  };

  const handleToggleActive = async (t) => {
    try {
      await hubApi.adminUpdateTenant(t.id, { active: !t.active });
      load();
      showSnackbar?.(`Tenant ${!t.active ? "ativado" : "desativado"}`);
    } catch (err) {
      showSnackbar?.("Erro ao atualizar", "error");
    }
  };

  const handleSavePlan = async () => {
    if (!planTenant) return;
    setSavingPlan(true);
    try {
      await hubApi.adminUpdatePlan(planTenant.id, planTenant.plan);
      setPlanTenant(null);
      load();
      showSnackbar?.("Plano atualizado");
    } catch (err) {
      showSnackbar?.(err?.response?.data?.error || "Erro ao salvar plano", "error");
    } finally {
      setSavingPlan(false);
    }
  };

  const togglePlanChannel = (channelId) => {
    setPlanTenant((p) => ({
      ...p,
      plan: { ...(p.plan || {}), [channelId]: !p.plan?.[channelId] },
    }));
  };

  const setPerChannelMax = (channelId, value) => {
    setPlanTenant((p) => {
      const perCh = { ...(p.plan?.maxConnectionsPerChannel || {}) };
      const num = parseInt(value, 10);
      if (value === "" || isNaN(num)) {
        delete perCh[channelId]; // remover = usa o global
      } else {
        perCh[channelId] = Math.max(0, num);
      }
      return { ...p, plan: { ...p.plan, maxConnectionsPerChannel: perCh } };
    });
  };

  const setAllPlanChannels = (value, group = null) => {
    setPlanTenant((p) => {
      const newPlan = { ...(p.plan || {}) };
      ALL_CHANNELS.forEach((ch) => {
        if (!group || ch.group === group) newPlan[ch.id] = value;
      });
      return { ...p, plan: newPlan };
    });
  };

  const handleSaveEdit = async () => {
    try {
      await hubApi.adminUpdateTenant(editTenant.id, {
        name: editTenant.name,
        email: editTenant.email,
        webhookUrl: editTenant.webhookUrl,
      });
      setEditTenant(null);
      load();
      showSnackbar?.("Tenant atualizado");
    } catch (err) {
      showSnackbar?.("Erro ao salvar", "error");
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    showSnackbar?.(`${label} copiado!`);
  };

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: "#6B48FF" }} /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#fff" }}>HUBs Locais / Parceiros</Typography>
          <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
            Gerar chaves de acesso para parceiros conectarem ao Central
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Recarregar"><IconButton onClick={load} sx={{ color: "#6B7280" }}><Refresh /></IconButton></Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => { setCreateForm({ name: "", email: "", webhookUrl: "" }); setCreateOpen(true); setCreatedResult(null); }}
            sx={{
              background: "linear-gradient(135deg, #6B48FF, #8B5CF6)",
              textTransform: "none", fontWeight: 700, borderRadius: 2, px: 3,
            }}
          >
            Novo Tenant
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {tenants.map((t) => (
          <Grid item xs={12} md={6} key={t.id}>
            <Card sx={{ bgcolor: "#111827", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 3 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start", mb: 1.5 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#fff" }}>
                      #{t.id} {t.name}
                    </Typography>
                    {t.email && <Typography sx={{ fontSize: 12, color: "#9CA3AF" }}>{t.email}</Typography>}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {t.type === "central" ? (
                      <Chip size="small" label="Central" sx={{ fontSize: 10, height: 22, bgcolor: "rgba(107,72,255,0.15)", color: "#A78BFA" }} />
                    ) : t.type === "hub_local" ? (
                      <Chip size="small" label={`HUB Local${t.hubStatus === "online" ? " · online" : " · offline"}`} sx={{ fontSize: 10, height: 22, bgcolor: "rgba(37,211,102,0.12)", color: t.hubStatus === "online" ? "#25D366" : "#9CA3AF" }} />
                    ) : (
                      <Chip size="small" label="Cliente local" sx={{ fontSize: 10, height: 22, bgcolor: "rgba(156,163,175,0.12)", color: "#9CA3AF" }} />
                    )}
                    <Chip
                      size="small"
                      label={t.active ? "Ativo" : "Inativo"}
                      icon={t.active ? <CheckCircle sx={{ fontSize: 12, color: "#25D366 !important" }} /> : <Error sx={{ fontSize: 12, color: "#EF4444 !important" }} />}
                      sx={{
                        fontSize: 10, height: 22,
                        bgcolor: t.active ? "rgba(37,211,102,0.1)" : "rgba(239,68,68,0.1)",
                        color: t.active ? "#25D366" : "#EF4444",
                      }}
                    />
                    <Switch
                      size="small"
                      checked={t.active}
                      onChange={() => handleToggleActive(t)}
                      sx={{ "& .MuiSwitch-thumb": { bgcolor: t.active ? "#25D366" : "#6B7280" } }}
                    />
                  </Box>
                </Box>

                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: 11, color: "#6B7280" }}>Chave de Acesso</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.3 }}>
                    <Typography sx={{ fontSize: 12, color: "#D1D5DB", fontFamily: "monospace", flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {showKey[t.id] ? t.apiKeyFull : t.apiKey}
                    </Typography>
                    <Tooltip title={showKey[t.id] ? "Ocultar" : "Revelar"}>
                      <IconButton size="small" onClick={() => setShowKey((p) => ({ ...p, [t.id]: !p[t.id] }))} sx={{ color: "#6B7280" }}>
                        <VpnKey sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copiar chave">
                      <IconButton size="small" onClick={() => copyToClipboard(t.apiKeyFull, "Chave")} sx={{ color: "#6B7280" }}>
                        <ContentCopy sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {t.webhookUrl && (
                  <Box sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 11, color: "#6B7280" }}>Webhook URL</Typography>
                    <Typography sx={{ fontSize: 11, color: "#9CA3AF", wordBreak: "break-all" }}>{t.webhookUrl}</Typography>
                  </Box>
                )}

                {t.plan && (
                  <Box sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 11, color: "#6B7280", mb: 0.3 }}>Canais habilitados</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.3 }}>
                      {ALL_CHANNELS.filter((ch) => t.plan?.[ch.id]).map((ch) => (
                        <Chip key={ch.id} size="small" label={ch.label}
                          sx={{ height: 18, fontSize: 9, bgcolor: "rgba(37,211,102,0.1)", color: "#25D366" }} />
                      ))}
                      {ALL_CHANNELS.filter((ch) => t.plan?.[ch.id]).length === 0 && (
                        <Typography sx={{ fontSize: 10, color: "#6B7280", fontStyle: "italic" }}>Nenhum</Typography>
                      )}
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Edit sx={{ fontSize: 14 }} />}
                    onClick={() => setEditTenant({ ...t })}
                    sx={{ textTransform: "none", fontSize: 11, borderColor: "rgba(255,255,255,0.15)", color: "#9CA3AF" }}
                  >
                    Editar
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Tune sx={{ fontSize: 14 }} />}
                    onClick={() => setPlanTenant({ id: t.id, name: t.name, plan: t.plan || {} })}
                    sx={{ textTransform: "none", fontSize: 11, borderColor: "rgba(107,72,255,0.3)", color: "#8B5CF6" }}
                  >
                    Plano
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<VpnKey sx={{ fontSize: 14 }} />}
                    onClick={() => handleRegenerate(t.id, t.name)}
                    sx={{ textTransform: "none", fontSize: 11, borderColor: "rgba(245,158,11,0.3)", color: "#F59E0B" }}
                  >
                    Regenerar chave
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {tenants.length === 0 && (
          <Grid item xs={12}>
            <Card sx={{ bgcolor: "#111827", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 3, p: 4, textAlign: "center" }}>
              <Typography sx={{ color: "#6B7280", mb: 2 }}>Nenhum tenant cadastrado ainda.</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}
                sx={{ background: "linear-gradient(135deg, #6B48FF, #8B5CF6)", textTransform: "none" }}>
                Criar primeiro tenant
              </Button>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Dialog criar tenant */}
      <Dialog open={createOpen} onClose={() => { if (!creating) { setCreateOpen(false); setCreatedResult(null); } }} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: "#1a1d29", color: "#fff", borderRadius: 3 } }}>
        <DialogTitle sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          {createdResult ? "Tenant criado!" : "Novo Tenant"}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {createdResult ? (
            <Box>
              <Typography sx={{ fontSize: 13, color: "#9CA3AF", mb: 2 }}>
                Anote os dados abaixo. Por seguranca, eles sao exibidos uma unica vez (a chave pode ser regenerada depois).
              </Typography>
              <Box sx={{ p: 2, bgcolor: "rgba(107,72,255,0.06)", borderRadius: 2, mb: 2 }}>
                <Typography sx={{ fontSize: 11, color: "#6B7280" }}>Tenant ID</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontFamily: "monospace", fontSize: 16, color: "#fff" }}>{createdResult.tenantId}</Typography>
                  <IconButton size="small" onClick={() => copyToClipboard(String(createdResult.tenantId), "ID")} sx={{ color: "#6B7280" }}>
                    <ContentCopy sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ p: 2, bgcolor: "rgba(107,72,255,0.06)", borderRadius: 2, mb: 2 }}>
                <Typography sx={{ fontSize: 11, color: "#6B7280" }}>Chave de Acesso</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "#fff", wordBreak: "break-all", flex: 1 }}>
                    {createdResult.apiKey}
                  </Typography>
                  <IconButton size="small" onClick={() => copyToClipboard(createdResult.apiKey, "Chave")} sx={{ color: "#6B7280" }}>
                    <ContentCopy sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              </Box>
              <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                Envie estes dados ao parceiro para configurar no Multiflow PRO (modal OtimizAPP HUB).
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Nome do Tenant" placeholder="Ex: Multiflow Cliente XYZ" fullWidth
                value={createForm.name} onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                InputLabelProps={{ sx: { color: "rgba(255,255,255,0.5)" } }}
                InputProps={{ sx: { color: "#fff", bgcolor: "#252936", borderRadius: 2 } }}
              />
              <TextField
                label="Email (opcional)" type="email" placeholder="cliente@dominio.com.br" fullWidth
                value={createForm.email} onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                InputLabelProps={{ sx: { color: "rgba(255,255,255,0.5)" } }}
                InputProps={{ sx: { color: "#fff", bgcolor: "#252936", borderRadius: 2 } }}
              />
              <TextField
                label="Webhook URL" placeholder="https://vps-cliente.com.br/otimizapphub/webhook" fullWidth
                value={createForm.webhookUrl} onChange={(e) => setCreateForm((f) => ({ ...f, webhookUrl: e.target.value }))}
                helperText="URL do Multiflow PRO do cliente que receberá os webhooks"
                InputLabelProps={{ sx: { color: "rgba(255,255,255,0.5)" } }}
                InputProps={{ sx: { color: "#fff", bgcolor: "#252936", borderRadius: 2 } }}
                FormHelperTextProps={{ sx: { color: "#6B7280" } }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <Button onClick={() => { setCreateOpen(false); setCreatedResult(null); }} sx={{ color: "#9CA3AF", textTransform: "none" }}>
            {createdResult ? "Fechar" : "Cancelar"}
          </Button>
          {!createdResult && (
            <Button onClick={handleCreate} disabled={creating || !createForm.name} variant="contained"
              sx={{ background: "linear-gradient(135deg, #6B48FF, #8B5CF6)", textTransform: "none", fontWeight: 700 }}>
              {creating ? <CircularProgress size={16} /> : "Criar Tenant"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog editar */}
      <Dialog open={!!editTenant} onClose={() => setEditTenant(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: "#1a1d29", color: "#fff", borderRadius: 3 } }}>
        <DialogTitle sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>Editar Tenant #{editTenant?.id}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {editTenant && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField label="Nome" fullWidth value={editTenant.name || ""}
                onChange={(e) => setEditTenant({ ...editTenant, name: e.target.value })}
                InputLabelProps={{ sx: { color: "rgba(255,255,255,0.5)" } }}
                InputProps={{ sx: { color: "#fff", bgcolor: "#252936", borderRadius: 2 } }} />
              <TextField label="Email" type="email" fullWidth value={editTenant.email || ""}
                onChange={(e) => setEditTenant({ ...editTenant, email: e.target.value })}
                InputLabelProps={{ sx: { color: "rgba(255,255,255,0.5)" } }}
                InputProps={{ sx: { color: "#fff", bgcolor: "#252936", borderRadius: 2 } }} />
              <TextField label="Webhook URL" fullWidth value={editTenant.webhookUrl || ""}
                onChange={(e) => setEditTenant({ ...editTenant, webhookUrl: e.target.value })}
                InputLabelProps={{ sx: { color: "rgba(255,255,255,0.5)" } }}
                InputProps={{ sx: { color: "#fff", bgcolor: "#252936", borderRadius: 2 } }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <Button onClick={() => setEditTenant(null)} sx={{ color: "#9CA3AF", textTransform: "none" }}>Cancelar</Button>
          <Button onClick={handleSaveEdit} variant="contained"
            sx={{ background: "linear-gradient(135deg, #6B48FF, #8B5CF6)", textTransform: "none", fontWeight: 700 }}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog gerenciar plano */}
      <Dialog open={!!planTenant} onClose={() => !savingPlan && setPlanTenant(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: "#1a1d29", color: "#fff", borderRadius: 3 } }}>
        <DialogTitle sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          Plano do Tenant #{planTenant?.id} — {planTenant?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {planTenant && (
            <Box>
              <Typography sx={{ fontSize: 13, color: "#9CA3AF", mb: 2 }}>
                Selecione os canais habilitados para este tenant. Esta configuracao substitui a do marketplace externo.
              </Typography>

              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <Button size="small" onClick={() => setAllPlanChannels(true)}
                  sx={{ textTransform: "none", fontSize: 11, color: "#25D366" }}>
                  Ativar todos
                </Button>
                <Button size="small" onClick={() => setAllPlanChannels(false)}
                  sx={{ textTransform: "none", fontSize: 11, color: "#EF4444" }}>
                  Desativar todos
                </Button>
                <Button size="small" onClick={() => { setAllPlanChannels(false); setAllPlanChannels(true, "meta"); }}
                  sx={{ textTransform: "none", fontSize: 11, color: "#6B48FF" }}>
                  Somente Meta
                </Button>
              </Box>

              <Typography sx={{ fontSize: 11, color: "#6B48FF", fontWeight: 600, mb: 1, mt: 1 }}>
                Canais Meta
              </Typography>
              {ALL_CHANNELS.filter((c) => c.group === "meta").map((ch) => renderChannelRow(ch, planTenant, togglePlanChannel, setPerChannelMax))}

              <Typography sx={{ fontSize: 11, color: "#6B48FF", fontWeight: 600, mb: 1, mt: 2 }}>
                Outros canais
              </Typography>
              {ALL_CHANNELS.filter((c) => c.group === "outros").map((ch) => renderChannelRow(ch, planTenant, togglePlanChannel, setPerChannelMax))}

              <TextField
                label="Limite global (fallback)" type="number" fullWidth size="small" sx={{ mt: 2 }}
                value={planTenant.plan?.maxConnections ?? 10}
                onChange={(e) => setPlanTenant((p) => ({ ...p, plan: { ...p.plan, maxConnections: parseInt(e.target.value, 10) || 0 } }))}
                InputLabelProps={{ sx: { color: "rgba(255,255,255,0.5)" } }}
                InputProps={{ sx: { color: "#fff", bgcolor: "#252936", borderRadius: 2 } }}
                helperText="Usado para canais sem limite especifico definido acima"
                FormHelperTextProps={{ sx: { color: "#6B7280" } }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <Button onClick={() => setPlanTenant(null)} disabled={savingPlan} sx={{ color: "#9CA3AF", textTransform: "none" }}>Cancelar</Button>
          <Button onClick={handleSavePlan} disabled={savingPlan} variant="contained"
            sx={{ background: "linear-gradient(135deg, #6B48FF, #8B5CF6)", textTransform: "none", fontWeight: 700 }}>
            {savingPlan ? <CircularProgress size={16} /> : "Salvar Plano"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
